import { onConsentReady, hasVendorGrant } from './gdpr_consent'
import { getEmbedParent, getEnvironment, inBrowser } from 'shared/javascripts/util/environment'
import { deleteCookies } from 'shared/javascripts/util/cookies'
import { parsedFrontendConfig } from './frontend_config'

// This module mirrors meta_pixel.ts: the raw ttq snippet has no consent-awareness of its own (unlike
// GTM, which integrates with Google Consent Mode), so we initialize it lazily on the first push, which
// is only reached after the consent gate in tikTokPixelPush() below.

declare global {
  interface Window {
    ttq: {
      (...args: unknown[]): void
      load: (pixelId: string, options?: unknown) => void
      page: (...args: unknown[]) => void
      track: (...args: unknown[]) => void
      methods: string[]
      setAndDefer: (target: any, method: string) => void
      instance: (target: string) => unknown
      _i: Record<string, any>
      _t: Record<string, number>
      _o: Record<string, unknown>
    }
    TiktokAnalyticsObject: string
    tikTokPixelEvents: string[]
  }
}

const pixelId = parsedFrontendConfig('tikTokPixelId') as string | null
let tikTokPixelInitialized = false

if (!pixelId && inBrowser()) {
  window.tikTokPixelEvents = []
}

function ensureTikTokPixelInitialized(): void {
  if (tikTokPixelInitialized) {
    return
  }

  ;(function (w: any, d: Document, t: string) {
    if (w[t] && w[t].load) {
      return
    }
    w.TiktokAnalyticsObject = t
    const ttq: any = (w[t] = w[t] || [])
    ttq.methods = [
      'page',
      'track',
      'identify',
      'instances',
      'debug',
      'on',
      'off',
      'once',
      'ready',
      'alias',
      'group',
      'enableCookie',
      'disableCookie',
      'holdConsent',
      'revokeConsent',
      'grantConsent',
    ]
    ttq.setAndDefer = function (target: any, method: string) {
      target[method] = function () {
        target.push([method].concat(Array.prototype.slice.call(arguments, 0)))
      }
    }
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i])
    }
    ttq.instance = function (target: string) {
      const instance = ttq._i[target] || []
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(instance, ttq.methods[n])
      }
      return instance
    }
    ttq.load = function (pixel: string, options: any) {
      const url = 'https://analytics.tiktok.com/i18n/pixel/events.js'
      ttq._i = ttq._i || {}
      ttq._i[pixel] = []
      ttq._i[pixel]._u = url
      ttq._t = ttq._t || {}
      ttq._t[pixel] = +new Date()
      ttq._o = ttq._o || {}
      ttq._o[pixel] = options || {}
      const script: any = d.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src = `${url}?sdkid=${pixel}&lib=${t}`
      const existingScript = d.getElementsByTagName('script')[0]
      existingScript.parentNode!.insertBefore(script, existingScript)
    }
  })(window, document, 'ttq')

  window.ttq.load(pixelId!)
  tikTokPixelInitialized = true
}

function performTikTokPixelPush(): void {
  if (getEnvironment() === 'development' || getEnvironment() === 'staging') {
    console.log('[TikTok Pixel] Tracking event', { eventName: 'PageView' })
  }
  if (pixelId) {
    ensureTikTokPixelInitialized()
    window.ttq.page()
  } else {
    window.tikTokPixelEvents.push('PageView')
  }
}

export function tikTokPixelPush(eventName: string): void {
  if (eventName !== 'page.view') {
    return
  }
  if (getEmbedParent()) {
    return
  }

  onConsentReady(() => {
    if (hasVendorGrant('TikTok Ad Network')) {
      performTikTokPixelPush()
    }
  })
}

export function updateTikTokPixelConsent(): void {
  if (!pixelId || getEmbedParent()) {
    return
  }
  if (!hasVendorGrant('TikTok Ad Network')) {
    deleteCookies(
      (name) => name.startsWith('_tt') || name.startsWith('ttcsid') || name === 'ttclid',
    )
  }
}
